import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export interface PaginationControlsProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  testIdPrefix: string;
  itemLabel?: string;
  showPageSizeSelector?: boolean;
  className?: string;
  windowSize?: number;
}

export function PaginationControls({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  testIdPrefix,
  itemLabel = "results",
  showPageSizeSelector = true,
  className,
  windowSize = 5,
}: PaginationControlsProps) {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageButtons = useMemo(() => {
    let start = Math.max(1, safePage - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }, [safePage, totalPages, windowSize]);

  const startIdx = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIdx = Math.min(total, safePage * pageSize);

  const goTo = (p: number) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (target !== safePage) onPageChange(target);
  };

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 ${className ?? ""}`}
      data-testid={`${testIdPrefix}-pagination`}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-xs text-gray-500" data-testid={`${testIdPrefix}-page-info`}>
          {total === 0
            ? `0 ${itemLabel}`
            : `Showing ${startIdx}–${endIdx} of ${total} ${itemLabel}`}
        </div>
        {showPageSizeSelector && onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Per page</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => onPageSizeChange(parseInt(v))}
            >
              <SelectTrigger
                className="w-[80px] h-8 text-sm"
                data-testid={`${testIdPrefix}-select-page-size`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>
      {totalPages > 1 ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => goTo(1)}
            data-testid={`${testIdPrefix}-first-page`}
          >
            First
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage <= 1}
            onClick={() => goTo(safePage - 1)}
            data-testid={`${testIdPrefix}-prev-page`}
          >
            Previous
          </Button>
          {pageButtons.map((p) => (
            <Button
              key={p}
              type="button"
              variant={p === safePage ? "default" : "outline"}
              size="sm"
              onClick={() => goTo(p)}
              data-testid={`${testIdPrefix}-page-${p}`}
              className="min-w-[36px] px-2"
            >
              {p}
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => goTo(safePage + 1)}
            data-testid={`${testIdPrefix}-next-page`}
          >
            Next
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={safePage >= totalPages}
            onClick={() => goTo(totalPages)}
            data-testid={`${testIdPrefix}-last-page`}
          >
            Last
          </Button>
          <div className="flex items-center gap-1 ml-2">
            <span className="text-xs text-gray-500">Go to</span>
            <Input
              type="number"
              min={1}
              max={totalPages}
              defaultValue={safePage}
              key={`page-jump-${safePage}-${totalPages}`}
              className="w-[70px] h-8 text-sm"
              data-testid={`${testIdPrefix}-page-jump`}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const val = parseInt((e.target as HTMLInputElement).value);
                  if (Number.isFinite(val)) goTo(val);
                }
              }}
              onBlur={(e) => {
                const val = parseInt(e.target.value);
                if (Number.isFinite(val)) goTo(val);
              }}
            />
            <span className="text-xs text-gray-500">of {totalPages}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function clampPage(page: number, total: number, pageSize: number): number {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  if (!Number.isFinite(page) || page < 1) return 1;
  if (page > totalPages) return totalPages;
  return page;
}
